// Funnel handoff (TDC-1573): what the anonymous audit / strategy pages pass
// to /register, and what the register form sends back to the API.
//
// The link carries just enough for POST /auth/register to claim what the
// visitor already created (lib/funnelClaim on the API): the email they gave
// (prefills the form), the audited PDP URL (becomes the brand's first
// scored product), the audit report id (seeds that product's first score —
// only honoured server-side when the report belongs to the registrant's
// email), the strategy's share token (possession = ownership) and a
// Consumer Voice / AI Presence analysis id + access token (same rule: the
// token proves the analysis is the visitor's, whatever email they register
// with). Every product the visitor touched lands in the new account.

export interface AnalysisHandoff {
    id: number | string;
    token: string;
}

export interface RegisterHandoffInput {
    email?: string | null;
    url?: string | null;
    auditReportId?: number | string | null;
    strategyToken?: string | null;
    consumerVoice?: AnalysisHandoff | null;
    aiPresence?: AnalysisHandoff | null;
}

export interface RegisterFunnelBody {
    pdp_url?: string;
    pdp_audit_report_id?: number;
    proposal_token?: string;
    consumer_voice?: { id: number; token: string };
    ai_presence?: { id: number; token: string };
}

export interface RegisterHandoff {
    email: string;
    // null when the URL carried nothing the API could use.
    funnel: RegisterFunnelBody | null;
}

const clean = (v: string | number | null | undefined): string =>
    v === null || v === undefined ? "" : String(v).trim();

function setAnalysis(params: URLSearchParams, idKey: string, tokenKey: string, a?: AnalysisHandoff | null) {
    if (!a) return;
    const id = clean(a.id);
    const token = clean(a.token);
    if (/^\d+$/.test(id) && token) {
        params.set(idKey, id);
        params.set(tokenKey, token);
    }
}

function readAnalysis(
    searchParams: URLSearchParams,
    idKey: string,
    tokenKey: string
): { id: number; token: string } | null {
    const id = Number(clean(searchParams.get(idKey)));
    const token = clean(searchParams.get(tokenKey));
    return Number.isInteger(id) && id > 0 && token ? { id, token } : null;
}

export function buildRegisterHandoffPath(input: RegisterHandoffInput = {}): string {
    const params = new URLSearchParams();
    const email = clean(input.email);
    const url = clean(input.url);
    const audit = clean(input.auditReportId);
    const strategy = clean(input.strategyToken);
    if (email) params.set("email", email);
    if (url) params.set("url", url);
    if (audit && /^\d+$/.test(audit)) params.set("audit", audit);
    if (strategy) params.set("strategy", strategy);
    setAnalysis(params, "cv", "cvt", input.consumerVoice);
    setAnalysis(params, "aip", "aipt", input.aiPresence);
    const query = params.toString();
    return query ? `/register?${query}` : "/register";
}

export function readRegisterHandoff(searchParams: URLSearchParams): RegisterHandoff {
    const email = clean(searchParams.get("email"));
    const url = clean(searchParams.get("url"));
    const auditRaw = clean(searchParams.get("audit"));
    const strategy = clean(searchParams.get("strategy"));
    const funnel: RegisterFunnelBody = {};
    if (/^https?:\/\//i.test(url)) funnel.pdp_url = url;
    const auditId = Number(auditRaw);
    if (Number.isInteger(auditId) && auditId > 0) funnel.pdp_audit_report_id = auditId;
    if (strategy) funnel.proposal_token = strategy;
    const consumerVoice = readAnalysis(searchParams, "cv", "cvt");
    if (consumerVoice) funnel.consumer_voice = consumerVoice;
    const aiPresence = readAnalysis(searchParams, "aip", "aipt");
    if (aiPresence) funnel.ai_presence = aiPresence;
    return {
        email,
        funnel: Object.keys(funnel).length ? funnel : null
    };
}

// "amazon.com" for the register form's "we'll add … to your catalog" note.
export function handoffHostname(url: string | undefined | null): string {
    if (!url) return "";
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    } catch {
        return "";
    }
}

export interface StrategyHandoffInput {
    title?: string | null;
    url?: string | null;
    brand?: string | null;
    email?: string | null;
    auditReportId?: number | string | null;
    category?: number | string | null;
    score?: number | string | null;
    // The analysis the strategy is suggested from (its id + access token).
    consumerVoice?: AnalysisHandoff | null;
    aiPresence?: AnalysisHandoff | null;
}

// The public strategy builder link (/proposal) every anonymous tool hands
// off to: the product it is about, the visitor's email (skips the email
// gate) and what to suggest from.
export function buildStrategyPath(base: string, input: StrategyHandoffInput = {}): string {
    const params = new URLSearchParams();
    const set = (key: string, v: string | number | null | undefined) => {
        const value = clean(v);
        if (value) params.set(key, value);
    };
    set("title", input.title);
    set("url", input.url);
    set("brand", input.brand);
    set("email", input.email);
    const audit = clean(input.auditReportId);
    if (/^\d+$/.test(audit)) params.set("audit", audit);
    set("category", input.category);
    set("score", input.score);
    if (input.consumerVoice) {
        set("cv", input.consumerVoice.id);
        set("t", input.consumerVoice.token);
    } else if (input.aiPresence) {
        set("aip", input.aiPresence.id);
        set("t", input.aiPresence.token);
    }
    const query = params.toString();
    return query ? `${base}?${query}` : base;
}
